import { Checkbox } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { useTranslation } from "react-i18next";
import { patientProfileState } from "../../recoil/atoms";
import { getSpecificFetalSex, getUserAuth } from "../../recoil/selectors";
import { UserProfileAuthClientResponse } from "../../recoil/types";
import { networkErrorMessage } from "../../utils/helpers";
import { validateEmail } from "../../utils/hooks/helpers";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { GetPhiRequest } from "../Profile/Services/UpdateProfileInfoService";
import { StyledButton } from "../shared/Button/Button.styles";
import { ErrorPopup } from "../shared/ErrorPopup/ErrorPopup";
import { SpinnerLoading } from "../shared/Spinner/SpinnerLoading";
import { GetTypographyContent } from "../shared/Typography/Typography";
import {
  DetailedResultsFetalSexForwardInput,
  DetailedResultsFetalSexForwardWrapper,
  ActionAndCancelButtonPair,
  DetailedResultsForwardFetalSexTerms,
  DetailedResultsTypographyWrapper,
  ModalTextButton,
} from "./DetailedResults.styles";
import { ForwardFetalSexEmailRequest } from "./Services/DetailedResultsService";
import { TrackAnalyticsEvent } from "../../services/Analytics";
import { AnalyticsEventNames } from "../../utils/analyticsEvents";

export const DetailedResultsForwardFetalSex: React.FC<{
  setFetalSexSent: (sent: boolean) => void;
  setForwardFetalSex: (forward: boolean) => void;
  setConfirmedEmail: (email: string) => void;
  requsitionID: string;
}> = ({
  setFetalSexSent,
  setForwardFetalSex,
  setConfirmedEmail,
  requsitionID,
}) => {
  const { deviceType } = useDeviceHook();
  const { t } = useTranslation();
  const [forwardName, setForwardName] = useState("");
  const [forwardEmail, setForwardEmail] = useState("");
  const [forwardEmailError, setForwardEmailError] = useState("");
  const [forwardNameError, setForwardNameError] = useState("");
  const [responseMessage, setResponseMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const auth = useRecoilValue(getUserAuth);
  const fetalSex = useRecoilValue(getSpecificFetalSex(requsitionID));
  const [profileInfo, setPatientInfo] = useRecoilState(patientProfileState);

  useEffect(() => {
    const getProfileInfo = async () => {
      const response = await GetPhiRequest(auth?.access_token);
      if (response?.ok) {
        const profile =
          (await response.json()) as UserProfileAuthClientResponse;
        setPatientInfo(profile);
      }
    };
    if (!profileInfo && auth) {
      getProfileInfo();
    }
  }, [profileInfo, auth]);

  useEffect(() => {
    if (auth?.access_token) {
      TrackAnalyticsEvent(
        AnalyticsEventNames.impression,
        auth?.access_token,
        auth?.user_id,
        {
          impression_type: "page_view",
          page_name: "ForwardFetalSexPage",
          source: "AneuploidyReportPage",
        },
      );
    }
  }, []);

  const getLoadingOrMessage = () => {
    if (isLoading) {
      return <SpinnerLoading />;
    }
    if (responseMessage) {
      return (
        <ErrorPopup
          message={responseMessage}
          hideMessage={setResponseMessage}
        />
      );
    }
  };

  const submitForward = async (email: string) => {
    if (auth?.access_token) {
      TrackAnalyticsEvent(
        AnalyticsEventNames.interaction,
        auth?.access_token,
        auth?.user_id,
        {
          interaction_type: "button_click",
          button_text: "Forward Fetal Sex",
          element_name: "submit_forward_fetal_sex",
          page_name: "ForwardFetalSexPage",
        },
      );
    }
    let hasValidationError = false;
    if (!forwardName.trim()) {
      setForwardNameError(t("results.forwardFetalSex.errors.required"));
      hasValidationError = true;
    }
    if (!forwardEmail.trim()) {
      setForwardEmailError(t("results.forwardFetalSex.errors.required"));
      hasValidationError = true;
    } else if (!validateEmail(email.trim())) {
      setForwardEmailError(t("results.forwardFetalSex.errors.invalidEmail"));
      hasValidationError = true;
    }
    if (hasValidationError) {
      return;
    }
    if (profileInfo && auth && fetalSex) {
      setIsLoading(true);
      try {
        const response = await ForwardFetalSexEmailRequest(
          {
            req_identifier: requsitionID,
            recipients: [
              {
                to_email: forwardEmail.trim(),
                to_name: forwardName.trim(),
              },
            ],
          },
          auth?.access_token,
        );
        setIsLoading(false);
        if (response?.ok) {
          setConfirmedEmail(forwardEmail.trim());
          setFetalSexSent(true);
        } else {
          setResponseMessage(t("results.forwardFetalSex.errors.submit"));
        }
      } catch (e) {
        setIsLoading(false);
        setResponseMessage(networkErrorMessage);
      }
    }
  };

  return (
    <DetailedResultsFetalSexForwardWrapper deviceType={deviceType}>
      <div>
        {GetTypographyContent({
          content: t("results.forwardFetalSex.title"),
          size: "h3",
          textColor: "greyMedium",
          isBold: true,
        })}
      </div>
      <div>
        {GetTypographyContent({
          content: t("results.forwardFetalSex.instructions"),
          size: "large",
          textColor: "greyMedium",
        })}
      </div>
      <DetailedResultsFetalSexForwardInput
        label={t("results.forwardFetalSex.fields.name")}
        size="small"
        value={forwardName}
        error={forwardNameError}
        onChange={(e) => {
          setForwardNameError("");
          setForwardName(e.target.value);
        }}
      />
      <DetailedResultsFetalSexForwardInput
        label={t("results.forwardFetalSex.fields.email")}
        error={forwardEmailError}
        size="small"
        value={forwardEmail}
        onChange={(e) => {
          setForwardEmailError("");
          setForwardEmail(e.target.value);
        }}
      />
      {getLoadingOrMessage() || (
        <ActionAndCancelButtonPair>
          <StyledButton
            size="small"
            $backgroundColor="redMedium"
            $textColor="white"
            onClick={() => submitForward(forwardEmail)}
            disabled={!termsChecked}
          >
            {t("results.actions.forwardFetalSex")}
          </StyledButton>
          <ModalTextButton
            onClick={() => {
              setForwardFetalSex(false);
              if (auth?.access_token) {
                TrackAnalyticsEvent(
                  AnalyticsEventNames.interaction,
                  auth?.access_token,
                  auth?.user_id,
                  {
                    interaction_type: "button_click",
                    button_text: "Cancel",
                    element_name: "cancel_forward_fetal_sex",
                    page_name: "ForwardFetalSexPage",
                  },
                );
              }
            }}
          >
            {t("results.actions.cancel")}
          </ModalTextButton>
        </ActionAndCancelButtonPair>
      )}
      <DetailedResultsForwardFetalSexTerms>
        {profileInfo && (
          <>
            <Checkbox
              name="termsOfService"
              data-testid="termsOfServiceCheck"
              style={{ marginRight: 8 }}
              className="forward-fetal-checkbox"
              checked={termsChecked}
              onChange={(e) => setTermsChecked(e.target.checked)}
            />
            <DetailedResultsTypographyWrapper
              onClick={() => setTermsChecked(!termsChecked)}
            >
              {GetTypographyContent({
                content: t("results.forwardFetalSex.authorizationPrefix"),
                size: "small",
                textColor: "greyMedium",
              })}
              {GetTypographyContent({
                content: t("results.forwardFetalSex.authorizationLink"),
                size: "small",
                textColor: "greenMedium",
                link: "https://www.billiontoone.com/privacy-practices",
                externalLink: true,
              })}
            </DetailedResultsTypographyWrapper>
          </>
        )}
      </DetailedResultsForwardFetalSexTerms>
    </DetailedResultsFetalSexForwardWrapper>
  );
};
